<template>
  <b-card
    v-if="tableData"
    no-body
    class="status-statistics-card"
  >
    <b-card-header class="status-statistics-header">
      <h4 class="chart-title mb-0">
        Status Statistics
      </h4>
      <div class="header-actions">
        <small
          v-if="lastUpdatedAt"
          class="timestamp-badge"
          :class="{ 'clickable': onRefresh }"
          @click="handleRefresh"
        >
          <feather-icon
            icon="RefreshCwIcon"
            size="12"
            class="mr-1"
            :class="{ 'spin': isRefreshing }"
          />
          <span v-if="lastRefreshTimeAgo">
            {{ lastRefreshTimeAgo }}
          </span>
          <span v-else>
            {{ __dateTimeFormatterPassedTime(lastUpdatedAt) }}
          </span>
        </small>
      </div>
    </b-card-header>
    <b-card-body class="status-statistics-body">
      <b-table
        :items="tableData"
        responsive
        :fields="fields"
        class="mb-0 status-table"
      >
        <template #cell(order_count)="data">
          {{ (data.value).toLocaleString() }}
        </template>
        <template #cell(delivery_charge)="data">
          {{ __numberWithCommas(data.value, true) }}
        </template>
        <template #cell(ratio)="data">
          {{ roundToNearest(data.value) }}
        </template>
        <!-- statusIcon -->
        <template #cell(status_icon)="data">
          <div class="d-flex align-items-center">
            <b-avatar
              size="32"
              :style="{
                backgroundColor: getLightBackground(data.item.status_color),
                color: data.item.status_color,
              }"
            >
              <feather-icon
                size="20"
                :icon="data.value"
              />
            </b-avatar>
          </div>
        </template>
      </b-table>
    </b-card-body>
  </b-card>
</template>

<script>
import {
  BCard,
  BCardHeader,
  BCardBody,
  BTable,
  BAvatar,
} from 'bootstrap-vue'
import StatusMixin from '@core/mixins/status'

export default {
  components: {
    BCard,
    BCardHeader,
    BCardBody,
    BTable,
    BAvatar,
  },
  mixins: [StatusMixin],
  props: {
    tableData: {
      type: Array,
      default: () => [],
    },
    lastUpdatedAt: {
      type: String,
      default: null,
    },
    onRefresh: {
      type: Function,
      default: null,
    },
    isRefreshing: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      currentTime: Date.now(),
      updateInterval: null,
      fields: [
        {
          key: 'status_icon',
          label: 'Status Icon',
          sortable: true,
        },
        {
          key: 'status_name',
          label: 'Status Name',
          sortable: true,
        },
        {
          key: 'order_count',
          label: 'Number of Orders',
          sortable: true,
          thClass: 'text-right',
          tdClass: 'text-right',
        },
        {
          key: 'delivery_charge',
          label: 'Delivery Charge',
          sortable: true,
          thClass: 'text-right',
          tdClass: 'text-right',
        },
        {
          key: 'ratio',
          label: 'Ratio',
          sortable: true,
          thClass: 'text-right',
          tdClass: 'text-right',
        },
      ],
    }
  },
  computed: {
    lastRefreshTimeAgo() {
      try {
        const refreshTimes = JSON.parse(localStorage.getItem('dashboardwidgetsrefreshtimes') || '{}')
        const lastRefreshTime = refreshTimes.statusstatistics
        if (!lastRefreshTime) return null

        const lastRefresh = new Date(lastRefreshTime).getTime()
        const now = this.currentTime
        const diff = now - lastRefresh

        if (diff < 0) return null

        const seconds = Math.floor(diff / 1000)
        const minutes = Math.floor(seconds / 60)
        const hours = Math.floor(minutes / 60)
        const days = Math.floor(hours / 24)
        const months = Math.floor(days / 30)
        const years = Math.floor(months / 12)

        if (minutes < 1) return 'just now'
        if (minutes < 60) return `${minutes} minutes ago`
        if (hours < 24) return `${hours} hours ago`
        if (days < 30) return `${days} days ago`
        if (months < 12) return `${months} months ago`
        return `${years} years ago`
      } catch (e) {
        return null
      }
    },
  },
  mounted() {
    // Update countdown every second
    this.updateInterval = setInterval(() => {
      this.currentTime = Date.now()
    }, 1000)
  },
  beforeDestroy() {
    if (this.updateInterval) {
      clearInterval(this.updateInterval)
    }
  },
  methods: {
    roundToNearest(ratio) {
      return `${Number(ratio).toFixed(2)}%`
    },
    handleRefresh() {
      if (this.onRefresh && !this.isRefreshing) {
        this.onRefresh()
      }
    },
  },
}
</script>

<style scoped>
.status-statistics-card {
  box-shadow: 0 4px 24px 0 rgba(34, 41, 47, 0.1);
  border-radius: 0.857rem;
  overflow: hidden;
  transition: all 0.3s ease;
}

.status-statistics-card:hover {
  box-shadow: 0 8px 32px 0 rgba(34, 41, 47, 0.15);
  transform: translateY(-2px);
}

.status-statistics-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.25rem 1.5rem;
  background: #fff;
}

.chart-title {
  font-size: 1.285rem;
  font-weight: 600;
  color: #5e5873;
  margin: 0;
  font-family: 'Roboto', sans-serif;
}

.header-actions {
  display: flex;
  align-items: center;
}

.timestamp-badge {
  display: flex;
  align-items: center;
  background-color: #f5f5f5;
  color: #6e6b7b;
  padding: 0.375rem 0.75rem;
  border-radius: 0.375rem;
  font-size: 0.75rem;
  font-weight: 500;
  white-space: nowrap;
  transition: all 0.2s ease;
  font-family: 'Roboto', sans-serif;
}

.timestamp-badge.clickable {
  cursor: pointer;
  user-select: none;
}

.timestamp-badge.clickable:hover {
  background-color: #e8e8e8;
  transform: scale(1.05);
}

.timestamp-badge.clickable:active {
  transform: scale(0.98);
}

.spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.status-statistics-body {
  padding: 0 1.5rem;
  overflow-x: auto;
}

.status-table ::v-deep thead th {
  background-color: #f8f9fa;
  border-bottom: 2px solid #e9ecef;
  color: #5e5873;
  font-weight: 600;
  font-size: 0.875rem;
  padding: 1rem;
  text-transform: none;
  font-family: 'Roboto', sans-serif;
}

.status-table ::v-deep tbody td {
  padding: 1rem;
  border-bottom: 1px solid #e9ecef;
  color: #6e6b7b;
  font-size: 0.875rem;
  font-family: 'Roboto', sans-serif;
}

.status-table ::v-deep tbody tr:hover {
  background-color: #f8f9fa;
}

.status-table ::v-deep .table-responsive {
  border: none;
}

@media (max-width: 768px) {
  .status-statistics-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.75rem;
  }
}
</style>
